// Credit-note door, step 1 (only when the door has no order): customer > order picker. Reuses the order-picker rules (creditableOrders / orderOptions / withTyped) and
// the customers + orders hook. Customer locked when the door came from a customer page. The chosen order then opens CreditNoteBody (the one credit note form).
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Combobox from '../../Combobox';
import DrawerFooter from '../../../shell/drawer/DrawerFooter';
import { formatDate, formatMoney } from '../../../utils/format';
import { useCreditNoteSources } from '../useCreditNoteData';
import { creditableOrders, orderOptions, withTyped } from '../creditNoteHelpers.mjs';

export default function CreditNotePicker({ partnerId = '', partnerName = '', lockPartner = false, onPick, onClose, onInteract }) {
  const { t } = useTranslation('avoir');
  const [customerId, setCustomerId] = useState(partnerId);
  const [operationId, setOperationId] = useState('');
  const [query, setQuery] = useState('');
  const src = useCreditNoteSources({ customerId, lockedOrder: '', reload: 0, query });
  const orders = useMemo(() => creditableOrders(src.rows, ''), [src.rows]);
  const customerOptions = src.customers.map((c) => ({ value: c.id, label: c.name }));
  if (partnerName && partnerId && !customerOptions.some((o) => o.value === partnerId)) customerOptions.push({ value: partnerId, label: partnerName });
  const sel = orders.find((o) => o.operation_id === operationId);
  const pickCustomer = (v) => { setCustomerId(v); setOperationId(''); onInteract && onInteract(); };
  const go = () => {
    if (!sel) return;
    const pid = sel.partner_id || customerId;
    onPick({ operationId: sel.operation_id, partnerId: pid, customerName: sel.customer_name || (customerOptions.find((o) => o.value === pid) || {}).label || '' });
  };
  const loading = src.status === 'loading';
  return (
    <>
      <div className="dr-body">
        <div className="hint" data-testid="cn-pick-intro">{t('pickIntro')}</div>
        <div className="form">
          <div className="field full">
            <label>{t('colCustomer')}{lockPartner && <span className="opt-lab">{t('locked')}</span>}</label>
            <Combobox options={customerOptions} value={customerId} onChange={pickCustomer} disabled={lockPartner}
              placeholder={loading ? t('loading') : t('customerPlaceholder')} style={{ display: 'block', width: '100%' }} data-testid="cn-pick-customer" />
          </div>
          <div className="field full">
            <label>{t('colOrder')}<span className="req">*</span></label>
            <Combobox options={withTyped(orderOptions(orders, { fmtDate: formatDate, fmtMoney: formatMoney, statusLabel: (s) => t('customerCreditNote:lifecycle.' + s, { defaultValue: s }) }), query)}
              value={operationId} onChange={(v) => { setOperationId(v); onInteract && onInteract(); }} onQueryChange={setQuery}
              placeholder={loading ? t('loading') : t('orderPlaceholder')} style={{ display: 'block', width: '100%' }} data-testid="cn-pick-order" />
          </div>
        </div>
      </div>
      <DrawerFooter onClose={onClose}
        primary={<button type="button" className="btn success" disabled={!sel} onClick={go} data-testid="cn-pick-go">{t('pickContinue')}</button>} />
    </>
  );
}
